import { useDispatch, useSelector } from "react-redux";
import { ProductAvailableTimeSummary } from "../../types/product";
import { clearTypeModal, clearTypeOfShowModalType, selectCurrentShowTypeOfModal, setTypeModal, setTypeOfShowModalType } from "../../features/modal/modalSlice";
import { clearSelectedProductToAddQty } from "../../features/product/productSlice";
import { clearAddQty, clearSelectedItemLoading, removeCategory, removeSize, selectProductDetails, selectQuantity, selectSelectedAddonsProduct, selectSelectedProductDetails, setAddQty, setClearAddons, setSelectCategory, setSelectedItemLoading, setSelectSize } from "../../features/guest/guestSlice";
import { handleApiError } from "../../utils/handleApiError";
import { useMutationData } from "../api/useMutationData";
import { logOutGuest, resetAuth, selectCurrentUserClientID, selectCurrentUserId } from "../../features/auth/authSlice";
import { useStoreTimer } from "../../utils/useStoreTimer";
import { showToast } from "../../toastify/Toast";
import { useDynamicQueryData } from "../api/useDynamicQueryData";
import { updateCBrew } from "../../utils/updateCpBrewLocal";
import { useRef } from "react";
import { isProductOrderable } from "../../utils/isProductOrderable";

export default function useGuestAction() {

    const dispatch = useDispatch();
    const guestId = useSelector(selectCurrentUserId)
    const clientId = useSelector(selectCurrentUserClientID)
    const addQty = useSelector(selectQuantity)
    const selectedProductDetails = useSelector(selectSelectedProductDetails)
    const isUpdate = selectedProductDetails?.type === 'update'
    const typeOfModalShow = useSelector(selectCurrentShowTypeOfModal);
    const { updateCartProduct, staffLogOut, removeItemCart, updateCartQty, isLoadingUpdateCartQty, addProductToCart, checkOutOrder } = useMutationData()
    const { availableProductOfClient, refetchMyOrders, guestCart, refetchGuestCart, } = useDynamicQueryData({guestId, clientId})
    const { storeOpen  } = useStoreTimer();
    const localData = JSON.parse(localStorage.getItem("cBrew") || "{}");
    const selectedProductAddons = useSelector(selectSelectedAddonsProduct)

    // DISPATCH
    const handleSelect = (cat: string) => {
        dispatch(setSelectCategory(cat))
    };
    
    const handleRemove = (cat: string) => {
        dispatch(removeCategory(cat));
    };

    const handleSelectSize = (size: string) => {
        dispatch(setSelectSize(size));
    };
    
    const handleRemoveSize = (size: string) => {
        dispatch(removeSize(size));
    };

    const handleCartModal = () => {
        if (typeOfModalShow === "viewCart") {
            dispatch(clearTypeOfShowModalType());
        } else {
            dispatch(setTypeOfShowModalType("viewCart"));
            refetchGuestCart();
        }
        dispatch(clearTypeModal())
    };

    const handleOrderModal = () => {
        if (typeOfModalShow === "viewOrder") {
            dispatch(clearTypeOfShowModalType());
        } else {
            dispatch(setTypeOfShowModalType("viewOrder"));
            refetchMyOrders()
        }
        dispatch(clearTypeModal())
    };

    const handleCloseModal = () => {
        dispatch(clearTypeModal());
        dispatch(clearSelectedProductToAddQty())
        dispatch(clearAddQty())
        dispatch(setClearAddons())
        dispatch(selectProductDetails({packageID: null, productID: null, sizeID: null, type: null, cartItemID: null}))
        if(selectedProductDetails.type === 'update') {
            dispatch(setTypeOfShowModalType('viewCart'))
        }
    }

    // ACTION
    const handleAddCart = (product: ProductAvailableTimeSummary) => {
        dispatch(clearTypeOfShowModalType());
        dispatch(selectProductDetails({packageID: product.packageID, productID: product.productID, sizeID: product.sizeId, type: 'new', cartItemID: null}))
        dispatch(setTypeModal("selectedItem"));
    };

    const handleQuantityChange = (type: "increment" | "decrement") => {
        if(!type) return
        let newQty = type === "increment" ? addQty + 1 : Math.max(addQty - 1, 1);
        dispatch(setAddQty(newQty))
    };

    //API 
    const handleAddToCart = async () => {
        if ( !clientId || !selectedProductDetails.productID || !selectedProductDetails.sizeID || addQty === 0 || !selectedProductDetails.type ) {
            return showToast("Missing parameter", "error");
        }

        const addonIDs = selectedProductAddons?.map(item => item.addonID) ?? [];

        try {
            if (selectedProductDetails.type === "update") {
                if (!selectedProductDetails.cartItemID) {
                    return showToast("Missing parameter", "error");
                }

                await updateCartProduct({
                    productID: selectedProductDetails.productID,
                    quantity: addQty,
                    clientID: clientId,
                    addonIDs,
                    cartItemID: selectedProductDetails.cartItemID,
                }).unwrap();
                dispatch(setTypeOfShowModalType('viewCart'))
                showToast("Updated successfully.", "success");
            } else {
                await addProductToCart({
                    productID: selectedProductDetails.productID,
                    quantity: addQty,
                    clientID: clientId,
                    sizeId: selectedProductDetails.sizeID,
                    addonIDs,
                }).unwrap();
                showToast("Successfully added to cart.", "success");
            }

            dispatch(clearTypeModal());
            dispatch(clearSelectedProductToAddQty());
            dispatch(setClearAddons());
            dispatch(clearAddQty())
            dispatch(selectProductDetails({ packageID: null, productID: null, sizeID: null, type: null, cartItemID: null,}));
        } catch (error) {
            handleApiError(
                error,
                selectedProductDetails.type === "update"
                    ? "Failed to update item."
                    : "Failed to add item."
            );
        }
    };

    const updatingQtyRef = useRef(false);
    const handleUpdateQty = async ( currentQty: number, productID: number, sizeId: number, typeOfUpdate: 'increase' | 'decrease', cartItemID: number) => {
        if(isLoadingUpdateCartQty) return
        if (updatingQtyRef.current) return;
        if (!guestId || !guestCart || !storeOpen || !clientId) return;
        
        if (typeOfUpdate === "decrease" && currentQty <= 1) return;

        updatingQtyRef.current = true;
        dispatch(setSelectedItemLoading(productID));
        try {
            const newQty = typeOfUpdate === "increase"
                ? currentQty + 1
                : currentQty - 1;

            await updateCartQty({ clientID: clientId, productID, quantity: newQty, cartItemID, }).unwrap();
        } catch (error) {
            handleApiError(error, 'Failed to add qty')
        } finally {
            updatingQtyRef.current = false;
            dispatch(clearSelectedItemLoading())
        }
    };

    const handleRemoveItemCart = async (productId: number, guestId: number, cartItemID: number) => {
        if(!storeOpen || !clientId || !guestId) return

        const removeCartReq = {
            clientID: clientId,
            cartItemID: cartItemID,
        }
        try {
            await removeItemCart(removeCartReq).unwrap();
        } catch (error) {
            handleApiError(error, 'Failed to delete item')
        }
    }

    const handlePlaceOrderCart = async () => {
        if (!guestId || !storeOpen) return
        try {
            // Build a lookup of products that are currently orderable.
            const availableProductIds = new Set(
                (availableProductOfClient?.data ?? [])
                    .filter(isProductOrderable)
                    .map(product => product.productID)
            );
            // Check if any selected add-on is inactive.
            const inactiveAddon = guestCart?.items
                ?.flatMap(item => item.addons ?? [])
                .find(addon => !addon.isActive);

            if (inactiveAddon) {
                return showToast(
                    `${inactiveAddon.addonName} is no longer available. Please update your cart before placing your order.`,
                    "warning"
                );
            }
            // Get only valid product IDs from the guest cart.
            const productIDs =
                guestCart?.items
                    ?.filter(item => availableProductIds.has(item.productID))
                    .map(item => item.productID) ?? [];

            if (productIDs.length === 0) {
                return showToast(
                    "There are no available products in your cart.",
                    "warning"
                );
            }

            await checkOutOrder({ sessionID: guestId, productID: productIDs }).unwrap();
            showToast('Checkout successful', 'success')
            refetchGuestCart()
            refetchMyOrders()
            dispatch(clearTypeOfShowModalType());
        } catch (error) {
            handleApiError(error, 'Checkout failed')
        }
        
    }

    const handleLogoutGuest = async () => {
        try {
            const token = localData.token;
            await staffLogOut().unwrap();
            updateCBrew({ token });
            window.location.replace(`/login?token=${token}`);
            dispatch(logOutGuest());
            dispatch(resetAuth());
            showToast('Logout successfully.', 'success')
        } catch (error) {
            showToast("Logout failed!", "error");
        }
    }
    //Bao091
    //Kevs0c1
    return {
        // DISPATCH
        handleSelect,
        handleRemove,
        handleSelectSize,
        handleRemoveSize,
        handleCartModal,
        handleOrderModal,
        handleCloseModal,

        // ACTION
        handleAddCart,
        handleQuantityChange,

        // API
        handleAddToCart,
        handleUpdateQty,
        handleRemoveItemCart,
        handlePlaceOrderCart,
        handleLogoutGuest,

        updatingQtyRef,
        isUpdate
    }
}